// ─────────────────────────────────────────────
// Shared UI components — import from here
// Change styles here → updates everywhere
// ─────────────────────────────────────────────

// ── Style tokens (JS objects) ─────────────────
export const T = {
  // Inputs & selects
  input: {
    width: '100%',
    background: 'var(--surface)',
    border: '1px solid var(--border-md)',
    borderRadius: 'var(--radius)',
    padding: '9px 12px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: 'border-color .15s',
  },

  // Mono inputs (stock#, VIN, PO)
  inputMono: {
    width: '100%',
    background: 'var(--surface)',
    border: '1px solid var(--border-md)',
    borderRadius: 'var(--radius)',
    padding: '9px 12px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    fontFamily: 'JetBrains Mono, monospace',
    fontWeight: '500',
    boxSizing: 'border-box',
    letterSpacing: '0.5px',
    transition: 'border-color .15s',
  },

  // Field label
  label: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--muted)',
    marginBottom: '5px',
  },

  // Primary button
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'var(--blue)',
    border: 'none',
    borderRadius: 'var(--radius)',
    padding: '9px 18px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },

  // Secondary button
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'transparent',
    border: '1px solid var(--border-md)',
    borderRadius: 'var(--radius)',
    padding: '9px 14px',
    color: 'var(--muted)',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },

  // Danger button
  btnDanger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'transparent',
    border: '1px solid rgba(163,45,45,0.25)',
    borderRadius: 'var(--radius)',
    padding: '9px 14px',
    color: 'var(--red)',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Icon button
  btnIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid var(--border-md)',
    borderRadius: 'var(--radius)',
    padding: '5px 8px',
    color: 'var(--muted)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Table header
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--muted)',
    background: 'var(--bg)',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  },

  // Table cell
  td: {
    padding: '11px 14px',
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'middle',
    fontSize: '13px',
    color: 'var(--text)',
  },

  // Card
  card: {
    background: 'var(--surface)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    marginBottom: '14px',
  },

  // Page wrapper
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    fontFamily: 'Inter, sans-serif',
  },

  // Content area
  content: {
    maxWidth: '720px',
    margin: '0 auto',
    padding: '20px 16px 100px',
  },

  // Wide content area
  contentWide: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 16px 80px',
  },
};

// ── Reusable components ───────────────────────

// Page sub-header bar
export function PageHeader({ title, onBack, actions }) {
  return (
    <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'0 16px', height:'52px', display:'flex', alignItems:'center', gap:'10px' }}>
      {onBack && (
        <button onClick={onBack} style={T.btnSecondary}>
          <i className="ti ti-arrow-left" aria-hidden="true"/> Back
        </button>
      )}
      <span style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)' }}>{title}</span>
      {actions && <div style={{ marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center' }}>{actions}</div>}
    </div>
  );
}

// Section card with optional title
export function Section({ title, children, error, style, accent }) {
  const borderColor = error ? 'var(--red)' : 'var(--border)';
  const leftBorder  = accent ? (error ? '3px solid var(--red)' : '3px solid var(--blue)') : `1px solid ${borderColor}`;
  return (
    <div style={{ ...T.card, border:'none', borderTop:`1px solid ${borderColor}`, borderRight:`1px solid ${borderColor}`, borderBottom:`1px solid ${borderColor}`, borderLeft:leftBorder, ...style }}>
      {title && (
        <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)', background: error ? 'var(--red-bg)' : 'var(--surface)', display:'flex', alignItems:'center', gap:'8px' }}>
          {accent && !error && <div style={{ width:'3px', height:'14px', background:'var(--blue)', borderRadius:'2px', flexShrink:0, marginLeft:'-16px', marginRight:'8px' }}/>}
          <span style={{ fontSize:'11px', fontWeight:'600', color: error ? 'var(--red)' : 'var(--blue)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{title}</span>
          {error && typeof error === 'string' && <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--red)' }}>{error}</span>}
        </div>
      )}
      <div style={{ padding:'16px' }}>{children}</div>
    </div>
  );
}

// Form field wrapper
export function Field({ label, children, error, hint }) {
  return (
    <div style={{ marginBottom:'14px' }}>
      {label && <label style={{ ...T.label, color: error ? 'var(--red)' : 'var(--muted)' }}>{label}</label>}
      {children}
      {hint && !error && <p style={{ fontSize:'11px', color:'var(--muted)', marginTop:'4px' }}>{hint}</p>}
      {error && typeof error === 'string' && <p style={{ fontSize:'11px', color:'var(--red)', marginTop:'4px' }}>{error}</p>}
    </div>
  );
}

// Status badge
export function Badge({ label, color, bg }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', background: bg, color: color, fontSize:'11px', fontWeight:'500', padding:'2px 9px', borderRadius:'20px', whiteSpace:'nowrap' }}>
      {label}
    </span>
  );
}

// Alert / info box
export function Alert({ type = 'info', children }) {
  const styles = {
    info:    { bg:'var(--blue-light)',  color:'var(--blue)',   border:'rgba(10,110,180,0.2)' },
    warning: { bg:'var(--amber-bg)',    color:'var(--amber)',  border:'rgba(133,79,11,0.2)'  },
    error:   { bg:'var(--red-bg)',      color:'var(--red)',    border:'rgba(163,45,45,0.2)'  },
    success: { bg:'var(--green-bg)',    color:'var(--green)',  border:'rgba(15,110,86,0.2)'  },
  };
  const s = styles[type];
  return (
    <div style={{ background:s.bg, border:`1px solid ${s.border}`, borderRadius:'var(--radius)', padding:'10px 14px', color:s.color, fontSize:'13px', display:'flex', alignItems:'flex-start', gap:'8px', marginBottom:'14px' }}>
      <i className={`ti ti-${type==='error'?'alert-circle':type==='warning'?'alert-triangle':type==='success'?'circle-check':'info-circle'}`} style={{ flexShrink:0, marginTop:'1px' }} aria-hidden="true"/>
      <span>{children}</span>
    </div>
  );
}

// Empty state
export function Empty({ icon, title, subtitle, action }) {
  return (
    <div style={{ padding:'60px 20px', textAlign:'center' }}>
      {icon && <i className={`ti ${icon}`} style={{ fontSize:'40px', color:'var(--hint)', display:'block', marginBottom:'12px' }} aria-hidden="true"/>}
      <p style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'6px' }}>{title}</p>
      {subtitle && <p style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'16px' }}>{subtitle}</p>}
      {action}
    </div>
  );
}

// Loading state
export function Loading({ text = 'Loading...' }) {
  return (
    <div style={{ padding:'60px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', display:'block', marginBottom:'8px', color:'var(--blue)' }} aria-hidden="true"/>
      {text}
    </div>
  );
}

// Grid row for form fields
export function Row({ cols = 2, children }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols}, 1fr)`, gap:'12px' }}>
      {children}
    </div>
  );
}
